import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import { fetchSystemUsers, updateSystemUser, fetchUserGroups } from "../utils/api";

const EditSystemUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    employeeCode: "",
    gender: "Male",
    username: "",
    password: "",
    firebaseId: "null",
    status: "null",
    userGroup: {
      userGroupId: ""
    },
    reportsTo: "",
    target: ""
  });
  const [userGroups, setUserGroups] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [originalUser, setOriginalUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const navigateTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (navigateTimeoutRef.current != null) {
        clearTimeout(navigateTimeoutRef.current);
        navigateTimeoutRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const usersResponse = await fetchSystemUsers();
        if (cancelled) return;
        const userData = usersResponse.data?.find(u => u.userId === Number(id));

        if (!userData) {
          throw new Error('User not found');
        }

        // Set admin users list (excluding current user)
        setAdminUsers(usersResponse.data?.filter(u => u.userId !== Number(id)) || []);

        const groupsResponse = await fetchUserGroups();
        if (cancelled) return;
        setUserGroups(groupsResponse.data || []);

        // Remove password as we shouldn't pre-fill it
        const { password, ...userWithoutPassword } = userData;
        const sanitizedUser = {
          ...userWithoutPassword,
          password: '', // Empty password field
          userGroup: userWithoutPassword.userGroup || { userGroupId: '' },
          reportsTo: userWithoutPassword.reportsTo || '',
          target: userWithoutPassword.target || ''
        };

        setUser(sanitizedUser);
        setOriginalUser(userWithoutPassword);
      } catch (error) {
        console.error("Error fetching data:", error);
        if (!cancelled) setError("Failed to load user data. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "userGroupId") {
      setUser(prev => ({
        ...prev,
        userGroup: {
          userGroupId: value
        }
      }));
    } else {
      setUser(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    
    try {
      const submitData = { 
        ...user,
        reportsTo: user.reportsTo ? Number(user.reportsTo) : null,
        target: user.target ? Number(user.target) : null
      };
      if (!submitData.password) {
        delete submitData.password;
      }

      await updateSystemUser(id, submitData, {
        previousData: originalUser,
        identifier: `${originalUser?.firstName || submitData.firstName || ''} ${originalUser?.lastName || submitData.lastName || ''}`.trim() || submitData.username
      });
      setSuccessMessage("User updated successfully!");
      if (navigateTimeoutRef.current != null) clearTimeout(navigateTimeoutRef.current);
      navigateTimeoutRef.current = setTimeout(() => {
        navigateTimeoutRef.current = null;
        navigate("/dashboard/system-users");
      }, 2000);
    } catch (error) {
      console.error("Error updating user:", error);
      setError("Failed to update user. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/system-users");
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 4, p: 2 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Edit System User
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            label="First Name"
            name="firstName"
            value={user.firstName || ""}
            onChange={handleInputChange}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            label="Last Name"
            name="lastName"
            value={user.lastName || ""}
            onChange={handleInputChange}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            label="Mobile Number"
            name="mobile"
            value={user.mobile || ""}
            onChange={handleInputChange}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            label="Employee Code"
            name="employeeCode"
            value={user.employeeCode || ""}
            onChange={handleInputChange}
          />

          <FormControl component="fieldset" margin="dense">
            <RadioGroup
              row
              name="gender"
              value={user.gender || "Male"}
              onChange={handleInputChange}
            >
              <FormControlLabel value="Male" control={<Radio />} label="Male" />
              <FormControlLabel value="Female" control={<Radio />} label="Female" />
              <FormControlLabel value="Other" control={<Radio />} label="Other" />
            </RadioGroup>
          </FormControl>
          
          <TextField
            margin="normal"
            required
            fullWidth
            label="Username"
            name="username"
            value={user.username || ""}
            onChange={handleInputChange}
          />
          
          <TextField
            margin="normal"
            fullWidth
            type="password"
            label="Password (leave blank to keep current)"
            name="password"
            value={user.password || ""}
            onChange={handleInputChange}
          />
          
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="user-group-label">User Group</InputLabel>
            <Select
              labelId="user-group-label"
              name="userGroupId"
              value={user.userGroup?.userGroupId || ""}
              label="User Group"
              onChange={handleInputChange}
            >
              {userGroups.map((group) => (
                <MenuItem key={group.userGroupId} value={group.userGroupId}>
                  {group.groupName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="reports-to-label">Reporting To</InputLabel>
            <Select
              labelId="reports-to-label"
              name="reportsTo"
              value={user.reportsTo || ""}
              label="Reporting To"
              onChange={handleInputChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {adminUsers.map((adminUser) => (
                <MenuItem key={adminUser.userId} value={adminUser.userId}>
                  {`${adminUser.firstName || ''} ${adminUser.lastName || ''}`.trim() || adminUser.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            margin="normal"
            fullWidth
            label="Target"
            name="target"
            type="number"
            value={user.target || ""}
            onChange={handleInputChange}
            inputProps={{ min: 0 }}
          />
          
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : "Save Changes"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default EditSystemUser; 